import React from 'react';
import Layout from '../components/layout/Layout';
import { Calendar, MapPin, Users, ArrowRight, Clock, Tag } from 'lucide-react';
import { events } from '../data/mock';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';

const EventsPage = () => {
  const getEventTypeColor = (type) => {
    switch (type) {
      case 'flagship': return 'bg-orange-500';
      case 'conference': return 'bg-blue-500';
      case 'competition': return 'bg-purple-500';
      case 'training': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getEventTypeLabel = (type) => {
    switch (type) {
      case 'flagship': return 'Flagship Event';
      case 'conference': return 'Conference';
      case 'competition': return 'Competition';
      case 'training': return 'Training';
      default: return 'Event';
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-white/20 text-white text-sm font-semibold rounded-full mb-4 md:mb-6 backdrop-blur-sm border border-white/30">
            Upcoming Events
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Events & Programs
          </h1>
          <p className="text-base md:text-xl text-white/90 max-w-3xl mx-auto px-4">
            Join any one of our events and get benefited with our perks. 
            All our events are open for Students, Educators and Schools.
          </p>
        </div>
      </section>

      {/* Featured Event - 1MILE */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="text-white text-center lg:text-left">
              <Badge className="bg-white/20 text-white mb-4">Featured Event</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-3 md:mb-4">1 MILE 2027</h2>
              <h3 className="text-xl md:text-2xl mb-3 md:mb-4 text-white/90">1 Million Entrepreneurs Initiative</h3>
              <p className="text-base md:text-lg text-white/80 mb-4 md:mb-6 leading-relaxed">
                Join the movement to create 1 million young entrepreneurs through STEAM education and innovation. 
                Limited entries from each district for better reach and networking.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-3 md:gap-4 mb-6 md:mb-8">
                <div className="flex items-center justify-center lg:justify-start">
                  <Calendar className="w-5 h-5 mr-2" />
                  <span className="text-sm md:text-base">June 15-20, 2027</span>
                </div>
                <div className="flex items-center justify-center lg:justify-start">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span className="text-sm md:text-base">Pan India (Virtual + Physical)</span>
                </div>
              </div>
              <Link to="/contact">
                <Button className="bg-white text-orange-600 hover:bg-gray-100 px-6 md:px-8 py-2 md:py-3 rounded-full font-semibold">
                  Register Now
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative mt-6 lg:mt-0">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl md:rounded-3xl p-6 md:p-8 border border-white/20">
                <div className="text-center text-white">
                  <div className="text-4xl md:text-6xl font-bold mb-2">1M</div>
                  <div className="text-lg md:text-xl">Entrepreneurs</div>
                  <div className="mt-4 md:mt-6 grid grid-cols-3 gap-2 md:gap-4">
                    <div className="bg-white/10 rounded-lg md:rounded-xl p-2 md:p-4">
                      <div className="text-xl md:text-2xl font-bold">700+</div>
                      <div className="text-xs md:text-sm text-white/70">Districts</div>
                    </div>
                    <div className="bg-white/10 rounded-lg md:rounded-xl p-2 md:p-4">
                      <div className="text-xl md:text-2xl font-bold">28</div>
                      <div className="text-xs md:text-sm text-white/70">States</div>
                    </div>
                    <div className="bg-white/10 rounded-lg md:rounded-xl p-2 md:p-4">
                      <div className="text-xl md:text-2xl font-bold">100+</div>
                      <div className="text-xs md:text-sm text-white/70">Mentors</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              All Upcoming Events
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
              Explore our calendar of events designed for students, educators, and institutions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {events.map((event) => (
              <div
                key={event.id}
                className="bg-white rounded-xl md:rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 group"
              >
                <div className="p-6 md:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
                    <Badge className={`${getEventTypeColor(event.type)} text-white w-fit`}>
                      {getEventTypeLabel(event.type)}
                    </Badge>
                    {event.registrationOpen && (
                      <span className="text-green-600 text-sm font-medium">Open for Registration</span>
                    )}
                  </div>

                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {event.title}
                  </h3>
                  <p className="text-blue-600 font-medium mb-3 md:mb-4 text-sm md:text-base">{event.subtitle}</p>
                  <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">{event.description}</p>

                  <div className="flex flex-col sm:flex-row flex-wrap gap-2 md:gap-4 mb-4 md:mb-6 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                      {event.date}
                    </div>
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                      {event.location}
                    </div>
                  </div>

                  <Link to="/contact">
                    <Button className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white rounded-xl">
                      Register Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
            Want to Host an Event?
          </h2>
          <p className="text-base md:text-xl text-gray-300 mb-6 md:mb-8 px-4">
            Partner with STEAM-IE to bring world-class STEAM events to your institution.
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white px-8 md:px-10 py-4 md:py-6 text-base md:text-lg rounded-full">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default EventsPage;
